import { canReadProperties, coreQueryKey } from '@/api/core/authorization';
import { readNativeAccessInventory } from '@/api/core/native-access-inventory';
import { useOperatorSession } from '@/auth/runtime';
import { LoadFailure } from '@/components/load-failure';
import { formatDate, formatDateTimeWithOffset } from '@/lib/format';
import {
  Badge,
  Button,
  ConfirmDialog,
  SegmentedControl,
  SegmentedControlItem,
} from '@mgrvtechgroup/admin-ds';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { DevicePurposeEditor } from '../access/device-purpose-editor';
import { PurposeDraftContext, type PurposeDraftState } from '../access/purpose-draft-context';
import { propertyRead, type PropertyRuntime } from './property-queries';

/** 쪽을 옮기면 실제로 무엇이 사라지고 무엇이 남는지 그대로 알린다. */
function pageMoveConsequence(input: boolean, request: boolean) {
  const lostInput = '입력 중인 용도·사유는 저장되지 않고 사라집니다.';
  const keptRequest =
    '결과를 확인하지 못한 용도 변경 요청은 지워지지 않습니다. 이 쪽으로 돌아오면 같은 요청을 이어서 확인할 수 있지만, 새로고침하거나 로그아웃하면 이어서 확인할 수 없습니다.';
  return input && request ? `${lostInput} ${keptRequest}` : input ? lostInput : keptRequest;
}
/** 이 목록은 같은 탭 안의 객실 선택에는 그대로 남지만, 다른 탭으로 바꾸면 사라지므로 그 이동만 확인을 받는다. */
const leavesAccessTab = (a: { search: string }, b: { search: string }) =>
  new URLSearchParams(a.search).get('section') !== new URLSearchParams(b.search).get('section');
const LINK_STATE_LABEL = {
  CURRENT: '현재 연결',
  NO_MAPPING: '연결 없음',
  UNCERTAIN: '관계 확인 필요',
  UNCONFIRMED: '장비 확인 필요',
} as const;
const FILTER_LABEL = { CURRENT: '현재 연결', NO_MAPPING: '연결 없음', CHECK: '확인 필요' } as const;
type AccessFilter = 'all' | 'CURRENT' | 'NO_MAPPING' | 'CHECK';
/** 확인이 필요한 상태(관계·장비 미확정, 응답 누락)를 한 묶음으로 본다. */
function filterOf(state: keyof typeof LINK_STATE_LABEL | undefined): Exclude<AccessFilter, 'all'> {
  return state === 'CURRENT' || state === 'NO_MAPPING' ? state : 'CHECK';
}

/** Property-scoped saved links; provider application remains an independent observation. */
export function PropertyAccessOverview({
  runtime,
  propertyId,
  generation,
  onRoom,
}: {
  runtime: PropertyRuntime;
  propertyId: string;
  generation: number;
  onRoom: (room: string) => void;
}) {
  const session = useOperatorSession(runtime.session);
  const allowed =
    session.status === 'authorized' &&
    session.generation === generation &&
    !session.deniedDomains.includes('property') &&
    !!session.authorization &&
    canReadProperties(session.authorization);
  const [cursor, setCursor] = useState<string | null>(null);
  const [filter, setFilter] = useState<AccessFilter>('all');
  // 장비 용도 편집기에 저장하지 않은 입력이나 결과 미확정 요청이 있는 편집기들. 쪽 이동으로 그 행이 사라지기 전에 확인을 받는다.
  const [draftEditors, setDraftEditors] = useState<ReadonlyMap<string, PurposeDraftState>>(
    () => new Map(),
  );
  const reportDraft = useCallback((editorKey: string, state: PurposeDraftState | null) => {
    setDraftEditors((previous) => {
      const before = previous.get(editorKey);
      if (before?.input === state?.input && before?.request === state?.request) return previous;
      const next = new Map(previous);
      if (state) next.set(editorKey, state);
      else next.delete(editorKey);
      return next;
    });
  }, []);
  const [pendingChange, setPendingChange] = useState<(() => void) | null>(null);
  const guarded = (apply: () => void) => {
    if (draftEditors.size > 0) setPendingChange(() => apply);
    else apply();
  };
  const draftInput = [...draftEditors.values()].some((state) => state.input);
  const draftRequest = [...draftEditors.values()].some((state) => state.request);
  const current = () => runtime.session.getSnapshot().generation === generation;
  const query = useQuery({
    queryKey: [
      ...coreQueryKey(runtime.apiNamespace, generation, 'native-access-inventory', propertyId),
      cursor,
    ],
    queryFn: ({ signal }) =>
      propertyRead(
        runtime,
        generation,
        () => readNativeAccessInventory(runtime.client, propertyId, cursor, signal),
        signal,
        current,
      ),
    enabled: allowed,
    retry: false,
    gcTime: 0,
    meta: { suppressGlobalError: true },
  });
  if (!allowed) return null;
  const countOf = (group: Exclude<AccessFilter, 'all'>) =>
    (query.data?.core_slots ?? []).filter(
      (slot) =>
        filterOf(query.data?.items.find((item) => item.slot_id === slot.slot_id)?.state) === group,
    ).length;
  const shown = (query.data?.core_slots ?? []).filter(
    (slot) =>
      filter === 'all' ||
      filterOf(query.data?.items.find((item) => item.slot_id === slot.slot_id)?.state) === filter,
  );
  return (
    <section className="space-y-4" aria-label="지점 출입 연결">
      <ConfirmDialog
        target="다른 연결 쪽으로"
        action="이동"
        consequence={pageMoveConsequence(draftInput, draftRequest)}
        open={pendingChange !== null}
        onOpenChange={(open) => {
          if (!open) setPendingChange(null);
        }}
        onConfirm={() => {
          pendingChange?.();
          setPendingChange(null);
        }}
      />
      <h3 className="font-semibold">연결된 문과 사용 위치</h3>
      <p className="body-sm text-muted-foreground">이 지점에 저장된 문 연결과 사용 위치입니다.</p>
      <Badge variant="outline">실기기 반영 미확인</Badge>
      {query.isPending && <p role="status">출입 연결을 불러오고 있습니다.</p>}
      {query.isError && (
        <LoadFailure subject="출입 연결" error={query.error} onRetry={() => void query.refetch()} />
      )}
      {query.data && (
        <>
          <div className="body-sm text-muted-foreground">
            <p>조회일 {formatDate(query.data.observed_at)}</p>
            <details>
              <summary className="cursor-pointer">조회 시각 자세히</summary>
              <p>{formatDateTimeWithOffset(query.data.observed_at)}</p>
            </details>
          </div>
          {!query.data.core_slots.length && (
            <p>
              이 페이지에 조회된 슬롯 연결이 없습니다. 출입 그룹의 존재 여부는 별도 확인이
              필요합니다.
            </p>
          )}
          {query.data.core_slots.length > 0 && (
            <SegmentedControl
              size="sm"
              aria-label="출입 연결 상태 보기"
              value={filter}
              onValueChange={(next) => {
                setFilter(
                  next === 'CURRENT' || next === 'NO_MAPPING' || next === 'CHECK' ? next : 'all',
                );
              }}
            >
              <SegmentedControlItem value="all">
                전체 {query.data.core_slots.length}
              </SegmentedControlItem>
              {(['CURRENT', 'NO_MAPPING', 'CHECK'] as const).map((group) => (
                <SegmentedControlItem key={group} value={group}>
                  {FILTER_LABEL[group]} {countOf(group)}
                </SegmentedControlItem>
              ))}
            </SegmentedControl>
          )}
          {filter === 'CHECK' && (
            <p className="body-sm text-muted-foreground">
              ‘확인 필요’는 관계 확인 필요 · 장비 확인 필요 · 연결 응답 확인 실패인 슬롯입니다.
            </p>
          )}
          {filter !== 'all' && (
            <p className="body-sm text-muted-foreground">
              이번 페이지에서 고른 상태의 슬롯만 보고 있습니다. 다른 페이지의 슬롯은 포함하지
              않습니다.
            </p>
          )}
          {query.data.core_slots.length > 0 && !shown.length && (
            <p role="status">이 페이지에는 해당 상태의 슬롯이 없습니다.</p>
          )}
          <PurposeDraftContext.Provider value={reportDraft}>
            <ul className="divide-y rounded-md border empty:hidden">
              {query.data.core_slots.map((slot) => {
                const link = query.data.items.find((item) => item.slot_id === slot.slot_id);
                const device = link?.device;
                return (
                  <li
                    key={slot.slot_id}
                    // 걸러낸 행도 마운트해 둔다 — 장비 용도 초안·결과 불명 요청이 필터 전환으로 사라지지 않게.
                    hidden={!shown.includes(slot)}
                    className="space-y-2 p-3"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
                      <div className="flex min-w-0 flex-wrap items-baseline gap-x-3">
                        <h4 className="font-medium">
                          {slot.room_name ?? '객실명 미설정'} / {slot.name ?? '슬롯명 미설정'}
                        </h4>
                        <span>{link ? LINK_STATE_LABEL[link.state] : '연결 응답 확인 실패'}</span>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        aria-label={`이 객실의 연결 설정 · ${slot.room_name ?? '객실명 미설정'} / ${slot.name ?? '슬롯명 미설정'}`}
                        onClick={() => {
                          onRoom(slot.room_id);
                        }}
                      >
                        이 객실의 연결 설정
                      </Button>
                    </div>
                    {device && (
                      <p className="body-sm">
                        {device.device_type === 'DOOR'
                          ? '출입문'
                          : device.device_type === 'LOCK'
                            ? '잠금장치'
                            : device.device_type === null
                              ? '장비 분류 미설정'
                              : '출입 장비'}{' '}
                        · {device.is_active ? '활성 설정' : '비활성 설정'}
                      </p>
                    )}
                    {device?.core_device_id && (
                      <details>
                        <summary className="cursor-pointer">장비 용도 확인·수정</summary>
                        <DevicePurposeEditor
                          runtime={runtime}
                          id={device.core_device_id}
                          propertyId={propertyId}
                          guardNavigation
                          guardLeaves={leavesAccessTab}
                        />
                      </details>
                    )}
                  </li>
                );
              })}
            </ul>
          </PurposeDraftContext.Provider>
          {query.data.next_cursor && (
            <Button
              disabled={query.isFetching}
              variant="outline"
              onClick={() => {
                const nextCursor = query.data.next_cursor;
                guarded(() => {
                  setCursor(nextCursor);
                });
              }}
            >
              다음 연결
            </Button>
          )}
          {cursor && (
            <Button
              disabled={query.isFetching}
              variant="outline"
              onClick={() => {
                guarded(() => {
                  setCursor(null);
                });
              }}
            >
              처음 연결
            </Button>
          )}
        </>
      )}
    </section>
  );
}
